import { Injectable } from '@angular/core';
import { Router, Route } from '@angular/router'; // Re-added Router and Route for one-time parsing
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, concat, Subject } from 'rxjs';
import { delay, map, catchError } from 'rxjs/operators';
import { environment } from '@app/env-service';

export interface SearchResultItem {
  id: string | number;
  label: string;
  type: string;
  route?: string; // Will be populated for 'page' type from SearchablePage.navigationPath
}

export interface GroupedSearchResults {
  [groupName: string]: SearchResultItem[];
}

interface SearchablePage {
  id: string;         // e.g., 'page-dashboard', 'page-user-settings'
  label: string;      // "Dashboard", "User Settings"
  navigationPath: string; // Actual path like '/dashboard'
  keywords?: string[]; // Optional: For matching "home" to "/dashboard"
}


//

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchService {
  #useMockApi = false; // Set to true to use mock API data

  private requestSearchModalOpenSource = new Subject<void>();
  requestSearchModalOpen$ = this.requestSearchModalOpenSource.asObservable();

  // Static list of searchable pages - Labels adjusted as per user request
  private static readonly APP_PAGES_FOR_SEARCH: SearchablePage[] = [
    {
      "id": "page-dashboard",
      "label": "Dashboard",
      "navigationPath": "/dashboard",
      "keywords": ["dashboard", "home", "overview", "main", "stats"]
    },
    {
      "id": "page-inventory-skus",
      "label": "Inventory",
      "navigationPath": "/inventory/skus",
      "keywords": ["inventory", "skus", "stock", "items", "levels", "products"]
    },
     {
      "id": "page-inventory-stocks",
      "label": "Inventory Stocks",
      "navigationPath": "/inventory/stocks",
      "keywords": ["inventory", "stock levels", "quantities", "on hand"]
    },
    {
      "id": "page-inventory-adjustment-entries",
      "label": "Inventory Adjustment Entries",
      "navigationPath": "/inventory/adjustment-entries",
      "keywords": ["inventory", "adjustment", "entries", "corrections", "stock change"]
    },
    {
      "id": "page-inventory-adjustment-entries-create",
      "label": "Create Inventory Adjustment",
      "navigationPath": "/inventory/adjustment-entries/create",
      "keywords": ["inventory", "adjustment", "create", "new", "entry"]
    },
    {
      "id": "page-inventory-receiving",
      "label": "Inventory Receiving",
      "navigationPath": "/inventory/receiving",
      "keywords": ["inventory", "receiving", "inbound", "check in", "goods in"]
    },
    {
      "id": "page-inventory-transfer-order",
      "label": "Inventory Transfer Orders",
      "navigationPath": "/inventory/transfer-order/list",
      "keywords": ["inventory", "transfer", "order", "move stock", "internal"]
    },
    {
      "id": "page-inventory-transfer-order-create",
      "label": "Create Inventory Transfer Order",
      "navigationPath": "/inventory/transfer-order/create",
      "keywords": ["inventory", "transfer", "order", "create", "new", "move"]
    },
    {
      "id": "page-shipping-posted-orders",
      "label": "Sales Orders", // UPDATED LABEL
      "navigationPath": "/shipping/posted-orders",
      "keywords": ["shipping", "posted", "orders", "fulfillment", "shipments", "sales", "SO"]
    },
    {
      "id": "page-shipping-purgatory",
      "label": "Purgatory",
      "navigationPath": "/shipping/purgatory",
      "keywords": ["purgatory", "unallocated", "orders", "unmapped", "unapproved", "pending", "approval", "mapping", "shipping"]
    },
    {
      "id": "page-shipping-picklists",
      "label": "Shipping Picklists",
      "navigationPath": "/shipping/picklists",
      "keywords": ["shipping", "picklists", "picking", "warehouse"]
    },
    {
      "id": "page-channels",
      "label": "Channels",
      "navigationPath": "/channels",
      "keywords": ["channels", "marketplaces", "stores", "connections", "sales channels"]
    },
    {
      "id": "page-product-categories",
      "label": "Product Categories",
      "navigationPath": "/product-categories",
      "keywords": ["product", "categories", "types", "grouping"]
    },
    {
      "id": "page-shipping-matrix",
      "label": "Shipping Matrix / Carriers",
      "navigationPath": "/shipping-matrix",
      "keywords": ["shipping", "matrix", "carriers", "rates", "rules", "methods"]
    },
    {
      "id": "page-orders",
      "label": "Purchase Orders", // Kept this label based on navigateTo mapping
      "navigationPath": "/orders",
      "keywords": ["orders", "purchase orders", "po", "vendor", "supplier"]
    },
    {
      "id": "page-staff",
      "label": "Staff / Users",
      "navigationPath": "/staff",
      "keywords": ["staff", "users", "employees", "team", "members", "permissions"]
    },
    {
      "id": "page-locations",
      "label": "Locations / Warehouses",
      "navigationPath": "/locations",
      "keywords": ["locations", "warehouses", "stock locations", "bins"]
    },
    {
      "id": "page-clients",
      "label": "Clients / Customers",
      "navigationPath": "/clients",
      "keywords": ["clients", "customers", "accounts", "companies", "contacts"]
    },
    {
      "id": "page-sales-crm-companies",
      "label": "CRM Companies",
      "navigationPath": "/sales/crm/companies",
      "keywords": ["sales", "crm", "companies", "customers", "accounts", "organizations"]
    },
    {
      "id": "page-sales-crm-contacts",
      "label": "CRM Contacts",
      "navigationPath": "/sales/crm/contacts",
      "keywords": ["sales", "crm", "contacts", "people", "users"]
    },
    {
      "id": "page-sales-invoices",
      "label": "Invoices", // Kept this label
      "navigationPath": "/sales/view/invoices",
      "keywords": ["sales", "invoices", "billing", "payments", "sent"]
    },
    {
      "id": "page-sales-estimates",
      "label": "Sales Estimates / Quotes",
      "navigationPath": "/sales/view/estimates",
      "keywords": ["sales", "estimates", "quotes", "quotations", "proposals"]
    },
    {
      "id": "page-products",
      "label": "Products", // UPDATED LABEL (Simplified)
      "navigationPath": "/products",
      "keywords": ["products", "skus", "items", "catalog", "rfq"]
    },
    {
      "id": "page-factories",
      "label": "Factories / Suppliers",
      "navigationPath": "/factories",
      "keywords": ["factories", "suppliers", "vendors", "manufacturers"]
    },
    {
      "id": "page-messages",
      "label": "Messages",
      "navigationPath": "/messages",
      "keywords": ["messages", "communication", "chat", "internal"]
    },
    {
      "id": "page-reports",
      "label": "Reports",
      "navigationPath": "/reports",
      "keywords": ["reports", "analytics", "data", "summary", "statistics"]
    },
    {
      "id": "page-revenue",
      "label": "Revenue",
      "navigationPath": "/revenue",
      "keywords": ["revenue", "sales", "income", "financials"]
    },
    {
      "id": "page-settings",
      "label": "Settings",
      "navigationPath": "/settings",
      "keywords": ["settings", "configuration", "options", "preferences", "admin"]
    },
     {
      "id": "page-settings-profile",
      "label": "My Profile Settings",
      "navigationPath": "/settings/my-profile-settings",
      "keywords": ["settings", "profile", "account", "user", "my account"]
    },
    {
      "id": "page-settings-integrations",
      "label": "Integration Settings",
      "navigationPath": "/integrations",
      "keywords": ["settings", "integrations", "apps", "connections", "api"]
    },
    {
      "id": "page-variant-types",
      "label": "Variant Types",
      "navigationPath": "/variant-types",
      "keywords": ["variant", "types", "options", "attributes", "product options"]
    },
    {
      "id": "page-product-tags",
      "label": "Product Tags",
      "navigationPath": "/product-tags",
      "keywords": ["product", "tags", "labels", "categories", "grouping"]
    },
    {
      "id": "page-client-types",
      "label": "Client Types",
      "navigationPath": "/client-types",
      "keywords": ["client", "types", "customer types", "groups"]
    },
    {
      "id": "page-client-purchase-order-types",
      "label": "Invoice Types", 
      "navigationPath": "/client-purchase-order-types",
      "keywords": ["client", "customer", "purchase", "order", "types", "po types", "billing", "payments", "invoices"]
    },
    {
      "id": "page-units-of-measure",
      "label": "Units Of Measure",
      "navigationPath": "/units-of-measure",
      "keywords": ["units", "measure", "measurement", "uom", "weight", "dimension"]
    },
    {
      "id": "page-pricing",
      "label": "Pricing Rules",
      "navigationPath": "/pricing",
      "keywords": ["pricing", "rules", "price lists", "discounts"]
    },
    {
      "id": "page-warehouse-mobile-app",
      "label": "Warehouse Mobile App",
      "navigationPath": "/warehouse-mobile-app",
      "keywords": ["warehouse", "mobile", "app", "scanner", "pda"]
    },
    {
      "id": "page-automation-rules",
      "label": "Automation Rules",
      "navigationPath": "/automation-rules",
      "keywords": ["automation", "rules", "workflows", "triggers"]
    },
    {
      "id": "page-custom-fields",
      "label": "Custom Fields",
      "navigationPath": "/custom-fields",
      "keywords": ["custom", "fields", "attributes", "metadata"]
    },
    {
      "id": "page-templates-list",
      "label": "Templates",
      "navigationPath": "/templates/list",
      "keywords": ["templates", "documents", "print", "email"]
    },
     {
      "id": "page-templates-form",
      "label": "Create/Edit Template",
      "navigationPath": "/templates/form",
      "keywords": ["templates", "documents", "print", "email", "new", "create", "edit"]
    },
    {
      "id": "page-bom",
      "label": "Bill of Materials (BOM)",
      "navigationPath": "/bom",
      "keywords": ["bom", "bill of materials", "components", "assembly", "recipe"]
    },
    {
      "id": "page-assembly-orders",
      "label": "Assembly Orders",
      "navigationPath": "/assembly-orders",
      "keywords": ["assembly", "orders", "build", "manufacturing", "work orders", "create"]
    },
    {
      "id": "page-cost-layers",
      "label": "Cost Layers",
      "navigationPath": "/cost-layers",
      "keywords": ["cost", "layers", "inventory cost", "fifo", "lifo", "valuation"]
    },
  ];

  constructor(private http: HttpClient, private router: Router /* Re-added for list generation */) {
    // No longer need the generation logic in constructor
  }

  // Helper to convert kebab-case to Title Case
  #toTitleCaseHelper(str: string): string {
    if (!str) return '';
    return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  // Temporary method to parse routes for generating the static list
  #parseRoutesForStaticList(
    routes: Route[],
    currentPathParts: string[],
    currentLabelParts: string[],
    foundPages: SearchablePage[]
  ): void {
    for (const route of routes) {
      if (route.outlet || route.path === '**' || (route.path && route.path.includes(':'))) {
        continue; // Skip outlets, wildcard, and parameterized routes for the base list
      }

      const pathSegment = route.path !== undefined ? route.path : '';
      // Only add to path/label if pathSegment is not empty (handles root '' routes correctly)
      const newPathParts = pathSegment ? [...currentPathParts, pathSegment] : [...currentPathParts];
      
      let routeTitlePart = '';
      if (pathSegment) { // Only generate title part if there's a path segment
         routeTitlePart = route.data?.['title'] || this.#toTitleCaseHelper(pathSegment.split('/').pop() || pathSegment);
      }
      // Add title part only if it's non-empty, to avoid leading/trailing ' > '
      const newLabelParts = routeTitlePart ? [...currentLabelParts, routeTitlePart] : [...currentLabelParts];

      // Consider it a navigable page if it has component, loadChildren, or children (that aren't just param routes)
      if (route.component || route.loadChildren || (route.children && route.children.some(child => child.path && !child.path.includes(':')))) {
        const fullPath = newPathParts.join('/');
        // Ensure label isn't empty if newLabelParts was empty (e.g. for root component with children)
        const label = newLabelParts.length > 0 ? newLabelParts.join(' > ') : (this.#toTitleCaseHelper(fullPath) || 'Home'); 

        if (fullPath) { // Only add if there's a path
            foundPages.push({
                id: 'page-' + fullPath.replace(/[^a-zA-Z0-9\-]/g, '-').replace(/--+/g, '-').toLowerCase(), // More robust ID
                label: label,
                navigationPath: '/' + fullPath,
                keywords: [pathSegment] // Basic keyword from path
            });
        }
      }

      if (route.children && route.children.length > 0) {
        this.#parseRoutesForStaticList(route.children, newPathParts, newLabelParts, foundPages);
      }
      // Note: This simplified parser doesn't load `loadChildren` routes.
      // The generated list will primarily contain top-level routes and explicitly defined children.
    }
  }

  // Call this once manually (e.g. in constructor with a log) to get the base list
  generateRawPageList(): SearchablePage[] {
    const rawPages: SearchablePage[] = [];
    // Attempt to get routes under BaseComponent, or fall back to all routes
    const mainAppRoutes = this.router.config.find(r => r.component?.name === 'BaseComponent')?.children || this.router.config;
    this.#parseRoutesForStaticList(mainAppRoutes, [], [], rawPages);
    
    // Deduplicate based on navigationPath, preferring shorter labels if duplicates exist
    const uniquePages = new Map<string, SearchablePage>();
    for (const page of rawPages) {
        if (!uniquePages.has(page.navigationPath) || 
            (uniquePages.get(page.navigationPath) && uniquePages.get(page.navigationPath)!.label.length > page.label.length)) {
            uniquePages.set(page.navigationPath, page);
        }
    }
    return Array.from(uniquePages.values());
  }

  getAppPageRoutes(query: string): SearchResultItem[] {
    if (!query) {
      return [];
    }
    const lowerCaseQuery = query.toLowerCase().trim();
    if (!lowerCaseQuery) {
        return [];
    }
    const queryParts = lowerCaseQuery.split(/\s+/).filter(part => part.length > 0);
    if (queryParts.length === 0) {
        return [];
    }

    const matchedPages: SearchablePage[] = []; // Array to hold matches
    const maxPages = 7; // Limit

    for (const page of GlobalSearchService.APP_PAGES_FOR_SEARCH) {
        // Create a combined searchable text blob for the page
        let searchableText = page.label.toLowerCase();
        if (page.keywords) {
            searchableText += ' ' + page.keywords.join(' ').toLowerCase();
        }

        // Check if ALL parts of the query exist as substrings
        const isMatch = queryParts.every(part => searchableText.includes(part));

        if (isMatch) {
            matchedPages.push(page); // Add the match
            if (matchedPages.length === maxPages) {
                break; // Stop searching once we have enough matches
            }
        }
    }

    // Map the found (and limited) pages to SearchResultItem
    return matchedPages.map(page => ({
      id: page.id,
      label: page.label,
      type: 'page',
      route: page.navigationPath
    }));
  }

  #transformApiResults(apiResults: GroupedSearchResults): GroupedSearchResults {
    const transformedApiResults: GroupedSearchResults = {};
    for (const groupName in apiResults) {
      if (Object.prototype.hasOwnProperty.call(apiResults, groupName)) {
        transformedApiResults[groupName] = (apiResults as any)[groupName].map((item: any) => ({
          ...item,
          route: item.url,
        }));
      }
    }
    return transformedApiResults;
  }

  search(query: string): Observable<GroupedSearchResults> {
    if (!query) {
      return of({});
    }
    const pageResults = this.getAppPageRoutes(query);
    const initialPageGroup: GroupedSearchResults = {};
    if (pageResults.length > 0) {
      initialPageGroup['Pages'] = pageResults;
    }
    let apiObservable$: Observable<GroupedSearchResults>;
    if (this.#useMockApi) {
      const mockApiData = {
        MTShipstationOrder: [{ id: 101, label: "Order: SO-2023-001 (Customer: Alice Wonderland)", url: "/sales-orders/101", type: "MTShipstationOrder" },{ id: 102, label: "Order: SO-2023-002 (Customer: Bob The Builder)", url: "/sales-orders/102", type: "MTShipstationOrder" }],
        AssemblyOrder: [{ id: 201, label: "Assembly: AO-005 (Status: Completed)", url: "/assembly-orders/201", type: "AssemblyOrder" },{ id: 202, label: "Assembly: AO-006 (Status: In Progress)", url: "/assembly-orders/202", type: "AssemblyOrder" }],
        Picklist: [{ id: 301, label: "Picklist: PL-0010 (Status: Picked)", url: "/picklists/301", type: "Picklist" },{ id: 302, label: "Picklist: PL-0011 (Status: Pending)", url: "/picklists/302", type: "Picklist" }],
        RequestForQuote: [{ id: 401, label: "Product: SKU-XYZ-001 - Premium Widget", url: "/products/401", type: "RequestForQuote" },{ id: 402, label: "Product: SKU-ABC-002 - Standard Gadget", url: "/products/402", type: "RequestForQuote" }],
        MTPurchaseOrder: [{ id: 501, label: "PO: PO-2023-007 (Vendor: Supplier Inc.)", url: "/purchase-orders/501", type: "MTPurchaseOrder" },{ id: 502, label: "PO: PO-2023-008 (Vendor: Parts Co.)", url: "/purchase-orders/502", type: "MTPurchaseOrder" }]
      };
      apiObservable$ = of(this.#transformApiResults(mockApiData)).pipe(delay(300));
    } else {
      const params = new HttpParams().set('q', query);
      apiObservable$ = this.http.get<GroupedSearchResults>(`${environment.urlV2}/search`, { params }).pipe(
        map(apiResp => this.#transformApiResults(apiResp)),
        catchError(error => {
          console.error('Error fetching real search results:', error);
          const errorResult: GroupedSearchResults = {
            'API_Error': [{id: 'error', label: 'Could not fetch API results.', type: 'error'}]
          };
          return of(errorResult);
        })
      );
    }
    return concat(
      of(initialPageGroup),
      apiObservable$.pipe(
        map(apiResults => {
          const finalCombined: GroupedSearchResults = { ...initialPageGroup, ...apiResults };
          if (pageResults.length > 0) {
            finalCombined['Pages'] = pageResults;
          }
          return finalCombined;
        })
      )
    );
  }

  // TODO: Implement route parsing logic
  // searchRoutes(query: string): SearchResultItem[] { ... }

  triggerSearchModalOpen(): void {
    console.log('[GlobalSearchService] triggerSearchModalOpen called, about to .next()'); // DEBUG
    this.requestSearchModalOpenSource.next();
  }
} 